// src/components/ModalConsultorPrecios.js

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ImageNotSupportedOutlined as ImageNotSupportedIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import api from '../../api/api';

/* ──────────────────── Configuración ──────────────────── */

const BORDER_COLOR = '#333333';
const BACKGROUND_COLOR = '#333333';

const DEBOUNCE_MS = 500;
const SHOW_RESULT_MS = 5000;

/* ──────────────────── Estilos ──────────────────── */

const StyledDialog = styled(Dialog)`
  & .MuiDialog-container {
    padding: 16px;
    box-sizing: border-box;
  }

  & .MuiDialog-paper {
    width: min(1180px, calc(100vw - 32px));
    height: min(88vh, 860px);

    margin: auto;
    max-width: none;
    max-height: none;

    background: ${BACKGROUND_COLOR};
    border: 10px solid ${BORDER_COLOR};
    border-radius: 18px;

    box-sizing: border-box;
    position: relative;
    overflow: hidden;
  }

  @media (max-width: 800px) {
    & .MuiDialog-container {
      padding: 8px;
    }

    & .MuiDialog-paper {
      width: calc(100vw - 16px);
      height: calc(100vh - 16px);

      border-width: 6px;
      border-radius: 12px;
    }
  }
`;

const ContentWrapper = styled(Box)`
  flex: 1;
  min-width: 0;
  min-height: 0;

  margin: 18px;
  border-radius: 12px;

  background: #ffffff;

  display: flex;
  position: relative;
  overflow: hidden;

  @media (max-width: 800px) {
    margin: 10px;
  }
`;

const ResultWrapper = styled(Box)`
  width: 100%;
  height: 100%;

  min-width: 0;
  min-height: 0;

  display: flex;
  overflow: hidden;

  @media (max-width: 800px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

const ImagePanel = styled(Box)`
  flex: 0 0 48%;
  width: 48%;

  min-width: 380px;
  min-height: 0;

  padding: 30px;
  box-sizing: border-box;

  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 1),
    rgba(235, 235, 235, 1)
  );

  border-right: 1px solid #dddddd;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  @media (max-width: 800px) {
    flex: 0 0 auto;

    width: 100%;
    height: min(42vh, 360px);

    min-width: 0;
    min-height: 220px;

    padding: 16px;

    border-right: 0;
    border-bottom: 1px solid #dddddd;
  }
`;

const ProductImage = styled('img')`
  display: block;

  width: auto;
  height: auto;

  max-width: 100%;
  max-height: 100%;

  object-fit: contain;
  object-position: center;

  border-radius: 8px;
`;

const ProductInformation = styled(Box)`
  flex: 1;

  min-width: 0;
  min-height: 0;

  padding: 38px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;

  overflow-y: auto;

  @media (max-width: 800px) {
    flex: 0 0 auto;

    padding: 22px;
    gap: 20px;

    overflow: visible;
  }
`;

/* ──────────────────── Formateadores ──────────────────── */

const currencyFormatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const quantityFormatter = new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

/* ──────────────────── Componente ──────────────────── */

export default function ModalConsultorPrecios({ open, onClose }) {
  /* ───────────── Estados ───────────── */

  const [searchTerm, setSearchTerm] = useState('');
  const [producto, setProducto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const inputRef = useRef(null);
  const imageUrlRef = useRef(null);

  /* ───────────── Manejo de imagen ───────────── */

  const replaceImageUrl = useCallback((newUrl) => {
    const previousUrl = imageUrlRef.current;

    /*
     * Solo las URL creadas mediante URL.createObjectURL
     * deben ser revocadas.
     *
     * Las URL públicas del CDN no deben revocarse.
     */
    if (
      previousUrl &&
      previousUrl !== newUrl &&
      previousUrl.startsWith('blob:')
    ) {
      URL.revokeObjectURL(previousUrl);
    }

    imageUrlRef.current = newUrl;
    setImageUrl(newUrl);
  }, []);

  /* ───────────── Helpers ───────────── */

  const reset = useCallback(() => {
    setSearchTerm('');
    setProducto(null);
    setLoading(false);
    replaceImageUrl(null);

    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [replaceImageUrl]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const calcularPrecioBruto = useCallback((prod) => {
    const precioNeto = Number.parseFloat(prod?.precio_neto);

    if (!Number.isFinite(precioNeto)) {
      return 0;
    }

    return (prod?.impuestos || []).reduce(
      (precioAcumulado, impuesto) => {
        const porcentaje = Number.parseFloat(impuesto?.porcentaje);

        if (!Number.isFinite(porcentaje)) {
          return precioAcumulado;
        }

        return precioAcumulado + (precioNeto * porcentaje) / 100;
      },
      precioNeto,
    );
  }, []);

  const obtenerPrecioPresentacion = useCallback(
    (prod) => {
      const candidatos = [
        prod?.metricas?.precio_bruto_venta,
        prod?.precio_bruto_venta,
        prod?.precio_bruto,
        calcularPrecioBruto(prod),
      ];

      const precioValido = candidatos
        .map((precio) => Number(precio))
        .find((precio) => Number.isFinite(precio));

      return precioValido ?? 0;
    },
    [calcularPrecioBruto],
  );

  const obtenerUnidadPresentacion = useCallback((prod) => {
    const unidadDetalle = prod?.unidades?.unidad_venta;

    const clave = String(
      unidadDetalle?.clave ||
        prod?.unidad_venta ||
        prod?.unidad ||
        'UND',
    ).toUpperCase();

    const nombresPorClave = {
      UND: 'Unidad',
      UN: 'Unidad',
      UNI: 'Unidad',

      KG: 'Kilogramo',
      KILO: 'Kilogramo',

      G: 'Gramo',
      GR: 'Gramo',

      L: 'Litro',
      LT: 'Litro',

      ML: 'Mililitro',

      M: 'Metro',
      MT: 'Metro',

      CM: 'Centímetro',

      CAJ: 'Caja',
      CJ: 'Caja',

      PAQ: 'Paquete',
      PACK: 'Paquete',

      SAC: 'Saco',
      BOL: 'Bolsa',

      DOC: 'Docena',
    };

    return {
      clave,
      nombre:
        unidadDetalle?.nombre ||
        nombresPorClave[clave] ||
        clave,
    };
  }, []);

  const obtenerStockPresentacion = useCallback((prod) => {
    const candidatos = [
      prod?.stock_actual_venta,
      prod?.stock_actual,
    ];

    const stockValido = candidatos
      .map((stock) => Number(stock))
      .find((stock) => Number.isFinite(stock));

    return stockValido ?? 0;
  }, []);

  /* ───────────── Apertura y cierre ───────────── */

  useEffect(() => {
    if (open) {
      reset();
      return;
    }

    replaceImageUrl(null);
  }, [open, reset, replaceImageUrl]);

  /* ───────────── Limpieza al desmontar ───────────── */

  useEffect(() => {
    return () => {
      const currentUrl = imageUrlRef.current;

      if (currentUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(currentUrl);
      }

      imageUrlRef.current = null;
    };
  }, []);

  /* ───────────── Búsqueda con debounce ───────────── */

  useEffect(() => {
    const term = searchTerm.trim();

    if (!open || !term) {
      return undefined;
    }

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      setLoading(true);
      setProducto(null);
      replaceImageUrl(null);

      try {
        /*
         * 1. Buscar producto.
         */
        const searchResponse = await api.get(
          '/inventario_productos',
          {
            params: {
              search: term,
              page: 1,
              limit: 1,
            },
          },
        );

        if (cancelled) {
          return;
        }

        const productoListado =
          searchResponse?.data?.productos?.[0] || null;

        if (!productoListado) {
          setProducto(null);
          replaceImageUrl(null);
          return;
        }

        /*
         * 2. Consultar el detalle.
         *
         * Aquí recuperamos:
         * - unidad_venta
         * - unidades.unidad_venta
         * - metricas.precio_bruto_venta
         * - stock_actual_venta
         * - imagen pública
         */
        try {
          const detailResponse = await api.get(
            `/inventario_productos/${productoListado.id}/detalles`,
          );

          if (cancelled) {
            return;
          }

          const detalle = detailResponse?.data || {};
          const productoDetalle = detalle?.producto || {};

          const productoCompleto = {
            ...productoListado,
            ...productoDetalle,

            impuestos:
              detalle?.impuestos ||
              productoListado?.impuestos ||
              [],

            categorias:
              detalle?.categorias ||
              productoListado?.categorias ||
              [],

            unidades: detalle?.unidades || null,
            metricas: detalle?.metricas || null,

            stock_actual:
              detalle?.stock_actual ??
              productoListado?.stock_actual ??
              0,

            stock_actual_venta:
              detalle?.stock_actual_venta ??
              detalle?.stock_actual ??
              productoListado?.stock_actual ??
              0,

            precio_neto:
              detalle?.metricas?.precio_neto_venta ??
              productoDetalle?.precio_neto ??
              productoListado?.precio_neto,

            precio_bruto:
              detalle?.metricas?.precio_bruto_venta ??
              productoDetalle?.precio_bruto ??
              productoListado?.precio_bruto,

            precio_neto_venta:
              detalle?.metricas?.precio_neto_venta ??
              productoDetalle?.precio_neto ??
              productoListado?.precio_neto,

            precio_bruto_venta:
              detalle?.metricas?.precio_bruto_venta ??
              productoDetalle?.precio_bruto ??
              productoListado?.precio_bruto,

            unidad_venta:
              productoDetalle?.unidad_venta ??
              detalle?.unidades?.unidad_venta?.clave ??
              productoListado?.unidad_venta ??
              productoListado?.unidad,

            factor_venta:
              productoDetalle?.factor_venta ??
              detalle?.unidades?.factor_venta ??
              productoListado?.factor_venta ??
              1,
          };

          /*
           * Primero intentamos utilizar una URL pública.
           */
          const publicImageUrl =
            detalle?.imagen_principal?.url_publica ||
            detalle?.imagenes_publicas?.[0] ||
            detalle?.imagenes_detalle?.[0]?.url_publica ||
            productoListado?.imagen_principal?.url_publica ||
            productoListado?.primera_imagen_publica ||
            null;

          if (publicImageUrl) {
            replaceImageUrl(publicImageUrl);
            setProducto(productoCompleto);
            return;
          }

          /*
           * Respaldo: descargar la imagen como blob.
           */
          const imageFilename =
            detalle?.imagenes?.[0] ||
            detalle?.imagen_principal?.archivo_imagen ||
            productoListado?.primera_imagen ||
            null;

          if (imageFilename) {
            try {
              const imageResponse = await api.get(
                `/api/imagenes/${encodeURIComponent(imageFilename)}`,
                {
                  responseType: 'blob',
                },
              );

              const generatedUrl = URL.createObjectURL(
                imageResponse.data,
              );

              if (cancelled) {
                URL.revokeObjectURL(generatedUrl);
                return;
              }

              replaceImageUrl(generatedUrl);
            } catch (imageError) {
              console.error(
                'Error descargando imagen del producto:',
                imageError,
              );

              replaceImageUrl(null);
            }
          }

          setProducto(productoCompleto);
        } catch (detailError) {
          /*
           * Aunque falle el detalle, mostramos la información
           * disponible en el listado.
           */
          console.error(
            'Error cargando detalle del producto:',
            detailError,
          );

          if (!cancelled) {
            const fallbackImage =
              productoListado?.imagen_principal?.url_publica ||
              productoListado?.primera_imagen_publica ||
              null;

            replaceImageUrl(fallbackImage);
            setProducto(productoListado);
          }
        }
      } catch (searchError) {
        console.error(
          'Error buscando producto:',
          searchError,
        );

        if (!cancelled) {
          setProducto(null);
          replaceImageUrl(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);

          /*
           * Permite consultar nuevamente el mismo código.
           */
          setSearchTerm('');
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    open,
    searchTerm,
    replaceImageUrl,
  ]);

  /* ───────────── Ocultar resultado después de 5 segundos ───────────── */

  useEffect(() => {
    if (!producto) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      reset();
    }, SHOW_RESULT_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [producto, reset]);

  /* ───────────── Captura global del teclado ───────────── */

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handler = (event) => {
      /*
       * No bloquear combinaciones del sistema o navegador.
       */
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();

        handleClose();
        return;
      }

      const isCharacter = event.key.length === 1;
      const isBackspace = event.key === 'Backspace';
      const isEnter = event.key === 'Enter';

      if (
        !isCharacter &&
        !isBackspace &&
        !isEnter
      ) {
        return;
      }

      /*
       * Algunos lectores terminan el código con Enter.
       * No agregamos ese carácter al término de búsqueda.
       */
      if (isEnter) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      /*
       * Al comenzar una nueva consulta, limpiamos
       * inmediatamente el resultado anterior.
       */
      if (producto) {
        setProducto(null);
        replaceImageUrl(null);
      }

      setSearchTerm((previousTerm) => {
        if (isBackspace) {
          return previousTerm.slice(0, -1);
        }

        return previousTerm + event.key;
      });

      event.preventDefault();
      event.stopPropagation();

      if (
        typeof event.stopImmediatePropagation === 'function'
      ) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener(
      'keydown',
      handler,
      true,
    );

    return () => {
      document.removeEventListener(
        'keydown',
        handler,
        true,
      );
    };
  }, [
    open,
    producto,
    handleClose,
    replaceImageUrl,
  ]);

  /* ───────────── Valores de presentación ───────────── */

  const unidadPresentacion =
    obtenerUnidadPresentacion(producto);

  const precioPresentacion =
    obtenerPrecioPresentacion(producto);

  const stockPresentacion =
    obtenerStockPresentacion(producto);

  const marca =
    producto?.marca_nombre ||
    producto?.marca ||
    null;

  /* ───────────── Render ───────────── */

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="consultor-precios-title"
    >
      {/* Input invisible para mantener el foco */}
      <input
        ref={inputRef}
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: 'absolute',
          opacity: 0,
          height: 0,
          width: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Botón cerrar */}
      <IconButton
        onClick={handleClose}
        aria-label="Cerrar consultor de precios"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,

          color: '#ffffff',
          backgroundColor: 'rgba(0, 0, 0, 0.28)',

          zIndex: 10,

          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,

          height: '100%',
          minHeight: 0,

          display: 'flex',
          flexDirection: 'column',

          overflow: 'hidden',
        }}
      >
        <ContentWrapper>
          {/* Pantalla inicial */}
          {!loading && !producto && (
            <Box
              sx={{
                flex: 1,

                minWidth: 0,
                minHeight: 0,

                px: 3,

                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',

                gap: 2,
                textAlign: 'center',
              }}
            >
              <Typography
                id="consultor-precios-title"
                color="text.secondary"
                fontWeight={600}
                sx={{
                  fontSize: 'clamp(1.8rem, 4vw, 3.2rem)',
                  lineHeight: 1.15,
                }}
              >
                Consulte su precio aquí
              </Typography>

              <ArrowDownIcon
                sx={{
                  fontSize: 'clamp(48px, 8vw, 86px)',
                  color: 'text.secondary',
                }}
              />
            </Box>
          )}

          {/* Cargando */}
          {loading && (
            <Box
              sx={{
                flex: 1,

                minWidth: 0,
                minHeight: 0,

                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',

                gap: 2,
              }}
            >
              <CircularProgress size={64} />

              <Typography
                variant="h6"
                color="text.secondary"
              >
                Buscando producto...
              </Typography>
            </Box>
          )}

          {/* Resultado */}
          {!loading && producto && (
            <ResultWrapper>
              {imageUrl && (
                <ImagePanel>
                  <ProductImage
                    src={imageUrl}
                    alt={
                      producto.nombre ||
                      'Imagen del producto'
                    }
                    onError={() => {
                      replaceImageUrl(null);
                    }}
                  />
                </ImagePanel>
              )}

              <ProductInformation>
                {/* Nombre, marca y código */}
                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography
                    component="h2"
                    color="error"
                    fontWeight={800}
                    title={producto.nombre}
                    sx={{
                      fontSize: {
                        xs: 'clamp(1.6rem, 6vw, 2.5rem)',
                        md: 'clamp(2.1rem, 3.8vw, 3.8rem)',
                      },

                      lineHeight: 1.06,
                      letterSpacing: '-0.025em',

                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',

                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: {
                        xs: 4,
                        md: 4,
                      },

                      overflow: 'hidden',
                    }}
                  >
                    {producto.nombre}
                  </Typography>

                  {!!marca && (
                    <Typography
                      fontWeight={600}
                      sx={{
                        mt: 1.5,

                        fontSize:
                          'clamp(1rem, 1.8vw, 1.45rem)',

                        lineHeight: 1.2,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {marca}
                    </Typography>
                  )}

                  <Typography
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 1,

                      fontSize:
                        'clamp(0.8rem, 1.3vw, 1.05rem)',

                      overflowWrap: 'anywhere',
                    }}
                  >
                    Código de referencia:{' '}
                    {producto.sku || producto.id}
                  </Typography>
                </Box>

                {/* Precio por unidad de presentación */}
                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      fontSize:
                        'clamp(1rem, 1.6vw, 1.35rem)',

                      lineHeight: 1.2,
                    }}
                  >
                    Precio por{' '}
                    {unidadPresentacion.nombre.toLocaleLowerCase(
                      'es-CL',
                    )}
                  </Typography>

                  <Typography
                    component="div"
                    fontWeight={900}
                    sx={{
                      mt: 0.5,

                      fontSize: {
                        xs: 'clamp(2.4rem, 12vw, 4rem)',
                        md: 'clamp(3.2rem, 5.8vw, 5.8rem)',
                      },

                      lineHeight: 0.96,
                      letterSpacing: '-0.045em',

                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {currencyFormatter.format(
                      precioPresentacion,
                    )}
                  </Typography>

                  <Typography
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      display: 'block',
                      mt: 1.2,

                      fontSize:
                        'clamp(0.85rem, 1.3vw, 1.08rem)',
                    }}
                  >
                    Unidad de venta:{' '}
                    {unidadPresentacion.clave}
                  </Typography>
                </Box>

                {/* Stock */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',

                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      fontWeight={600}
                      sx={{
                        fontSize:
                          'clamp(0.95rem, 1.4vw, 1.15rem)',
                      }}
                    >
                      Stock disponible
                    </Typography>

                    <Typography
                      fontWeight={700}
                      sx={{
                        fontSize:
                          'clamp(1.5rem, 2.7vw, 2.4rem)',

                        lineHeight: 1.1,
                      }}
                    >
                      {quantityFormatter.format(
                        stockPresentacion,
                      )}{' '}
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.55em',
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                        }}
                      >
                        {unidadPresentacion.clave}
                      </Box>
                    </Typography>
                  </Box>

                  {!imageUrl && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',

                        gap: 1,
                        color: 'text.disabled',
                      }}
                    >
                      <ImageNotSupportedIcon />

                      <Typography
                        variant="caption"
                        sx={{
                          display: {
                            xs: 'none',
                            sm: 'block',
                          },
                        }}
                      >
                        Sin imagen
                      </Typography>
                    </Box>
                  )}
                </Box>
              </ProductInformation>
            </ResultWrapper>
          )}
        </ContentWrapper>
      </DialogContent>
    </StyledDialog>
  );
}

ModalConsultorPrecios.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};